import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal, Box, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Button, Avatar, IconButton, List, ListItem, ListItemText, InputAdornment
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { fetchCodes } from '../api/github';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  width: '95%',
  maxWidth: 600,
  p: 2,
  gap: 2,
  maxHeight: '90vh',
  overflowY: 'auto',
};

export default function AddReviewModal({ open, onClose, onAddReview, isSubmitting }) {
  const [newReview, setNewReview] = useState('');
  const [newNickname, setNewNickname] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newSource, setNewSource] = useState('client');
  const [newImage, setNewImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [codeInputType, setCodeInputType] = useState('select');
  const [codes, setCodes] = useState([]);
  const [codeSearch, setCodeSearch] = useState('');
  const [showCodeList, setShowCodeList] = useState(false);

  const codeSearchRef = useRef(null);

  const loadCodes = async () => {
    try {
      const codesData = await fetchCodes(true); // Получаем все коды, включая использованные
      setCodes(codesData || []);
    } catch (error) {
      console.error("Ошибка загрузки кодов:", error);
      setCodes([]);
    }
  };

  useEffect(() => {
    if (open) {
      loadCodes();
      setNewReview('');
      setNewNickname('');
      setNewCode('');
      setNewSource('client');
      setNewImage(null);
      setImagePreview(null);
      setCodeInputType('select');
      setCodeSearch('');
    }
  }, [open]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setNewImage(null);
    setImagePreview(null);
  };

  const handleCodeSearchChange = useCallback((e) => {
    setCodeSearch(e.target.value);
  }, []);

  const handleSubmit = () => {
    if (!newReview.trim() || !newNickname.trim() || !newCode.trim()) {
      alert('Илтимос, барча майдонларни тўлдиринг');
      return;
    }

    onAddReview({
      review: newReview,
      nickname: newNickname,
      code: newCode,
      source: newSource,
      image: newImage
    });

    setNewReview('');
    setNewNickname('');
    setNewCode('');
    setNewSource('client');
    setNewImage(null);
    setImagePreview(null);
    setCodeInputType('select');
    setCodeSearch('');
  };

  const CodeSelectionModal = () => {
    useEffect(() => {
      if (showCodeList && codeSearchRef.current) {
        codeSearchRef.current.focus();
      }
    }, [showCodeList]);

    return (
      <Modal open={showCodeList} disableAutoFocus onClose={() => setShowCodeList(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '95%',
          maxWidth: 500,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 2,
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <Typography variant="h6" gutterBottom>Код танлаш</Typography>
          
          <TextField
            inputRef={codeSearchRef}
            label="Код қидириш"
            fullWidth
            margin="normal"
            value={codeSearch}
            autoFocus
            onChange={handleCodeSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {codes
              .filter(codeItem => 
                codeItem.value.toLowerCase().includes(codeSearch.toLowerCase()) ||
                codeItem.description.toLowerCase().includes(codeSearch.toLowerCase())
              )
              .map(codeItem => (
                <ListItem 
                  key={codeItem.id} 
                  button
                  onClick={() => {
                    setNewCode(codeItem.value);
                    setShowCodeList(false);
                    setCodeSearch('');
                  }}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(0,0,0,0.1)'
                  }}
                >
                  <ListItemText
                    primary={codeItem.value}
                    secondary={codeItem.description}
                  />
                </ListItem>
              ))}
          </List>
          {codes.filter(codeItem => 
            codeItem.value.toLowerCase().includes(codeSearch.toLowerCase()) ||
            codeItem.description.toLowerCase().includes(codeSearch.toLowerCase())
          ).length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
              Кодлар топилмади
            </Typography>
          )}
        </Box>
      </Modal>
    );
  };

  return (
    <Modal open={open} onClose={() => !isSubmitting && onClose()}>
      <Box sx={style}>
        <Typography variant="h6" sx={{ mb: 2 }}>Янги отзыв қўшиш</Typography>
        <Grid sx={{gap: 2}}>
          <Grid sx={{ mb: 1 }}>
            <TextField
              label="Мижоз ники"
              fullWidth
              value={newNickname}
              onChange={(e) => setNewNickname(e.target.value)}
              disabled={isSubmitting}
              required
              size="small"
            />
          </Grid>
          
          <Grid>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                variant={codeInputType === 'select' ? 'contained' : 'outlined'}
                onClick={() => setCodeInputType('select')}
                size="small"
                fullWidth
              >
                Кодни танлаш
              </Button>
              <Button
                variant={codeInputType === 'manual' ? 'contained' : 'outlined'}
                onClick={() => setCodeInputType('manual')}
                size="small"
                fullWidth
              >
                Кодни киритиш
              </Button>
            </Box>
          </Grid>
          
          {codeInputType === 'select' ? (
            <Grid sx={{ mb: 1 }}>
              <FormControl fullWidth margin="normal" size="small" required>
                <InputLabel>Код</InputLabel>
                <Select
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  label="Код"
                  disabled={codes.length === 0 || isSubmitting}
                >
                  <MenuItem value="">
                    <em>Кодни танланг</em>
                  </MenuItem>
                  {codes.map(codeItem => (
                    <MenuItem 
                      key={codeItem.id} 
                      value={codeItem.value}
                      sx={{
                        color: codeItem.isUsed ? 'inherit' : 'text.disabled',
                        backgroundColor: codeItem.isUsed ? 'action.disabledBackground' : 'inherit'
                      }}
                    >
                      {codeItem.value} - {codeItem.description}
                    </MenuItem>
                  ))}
                </Select>
                {codes.length === 0 && (
                  <Typography variant="caption" color="error">
                    Барча кодлар ишлатилган. Янги код қўшинг ёки қолдиқ кутувдаги кодни киритинг.
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => {
                    setShowCodeList(true);
                    setCodeSearch('');
                  }}
                  disabled={isSubmitting || codes.length === 0}
                  sx={{ mt: 1 }}
                  fullWidth
                  size="small"
                >
                  Код қидириш
                </Button>
              </FormControl>
            </Grid>
          ) : (
            <Grid sx={{ mb: 1 }}>
              <TextField
                label="Код"
                fullWidth
                margin="normal"
                size="small"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </Grid>
          )}
          
          <Grid sx={{ mb: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Манба</InputLabel>
              <Select
                value={newSource}
                onChange={(e) => setNewSource(e.target.value)}
                label="Манба"
                disabled={isSubmitting}
              >
                <MenuItem value="client">Мижоз</MenuItem>
                <MenuItem value="mail">Почта</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid sx={{ mb: 1 }}>
            <TextField
              label="Отзыв"
              fullWidth
              multiline
              rows={4}
              value={newReview}
              onChange={(e) => setNewReview(e.target.value)}
              disabled={isSubmitting}
              required
              size="small"
            />
          </Grid>
          
          <Grid sx={{ mb: 1 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              disabled={isSubmitting}
              fullWidth
              size="small"
            >
              Отзыв расмини юклаш
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
            {imagePreview && (
              <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={imagePreview}
                  alt="Предпросмотр"
                  sx={{ width: 100, height: 100, borderRadius: 1 }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveImage}
                  disabled={isSubmitting}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: 'rgba(255,255,255,0.7)'
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Grid>
          
          <Grid sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                fullWidth
              >
                {isSubmitting ? 'Сақланияпти...' : 'Сақлаш'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => onClose()}
                disabled={isSubmitting}
                fullWidth
              >
                Бекор қилиш
              </Button>
            </Box>
          </Grid>
        </Grid>
        
        <CodeSelectionModal />
      </Box>
    </Modal>
  );
}